import { Box, Button, Field, Heading, Image, Input, Text, VStack } from "@chakra-ui/react"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useChangePassword } from "../../hooks/useAuthQueries"
import { getUserDetails } from "../../services/authApi"
import { getAuthErrorMessage } from "../../utils/authErrors"
import {
  clearMagicLinkSignIn,
  signedInViaMagicLink
} from "../../utils/magicLinkSession"
import { getPostLoginPath } from "../../utils/postLoginRoute"

const ForcePasswordChange: React.FC = () => {
  const navigate = useNavigate()
  // Read once on mount so a re-render (or a failed submit) can't flip the form
  // layout mid-flow. Cleared only after the password is successfully changed.
  const [viaMagicLink] = useState(signedInViaMagicLink)
  const [oldPassword, setOldPassword] = useState("")
  const [newPassword1, setNewPassword1] = useState("")
  const [newPassword2, setNewPassword2] = useState("")
  const mutation = useChangePassword()

  const getErrorMessage = (error: any) =>
    getAuthErrorMessage(error, ["old_password", "new_password1", "new_password2"])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await mutation.mutateAsync({
        ...(viaMagicLink ? {} : { oldPassword }),
        newPassword1,
        newPassword2
      })
      clearMagicLinkSignIn()
      const details = await getUserDetails()
      navigate(getPostLoginPath(details.data?.organisation), { replace: true })
    } catch {
      // handled via UI state
    }
  }

  return (
    <>
      <Box pb={8}>
        <Link to="/">
          <Image w={{ base: 150, md: 200 }} src="/greenflip_logo.png" alt="GreenFlip" />
        </Link>
      </Box>

      <Box
        bg="gray.100"
        px={{ base: 6, md: 8, lg: 12 }}
        py={{ base: 6, md: 8, lg: 10 }}
        borderRadius="3xl"
        w={{ base: "95%", md: "85%", lg: "80%" }}
        maxW="500px"
      >
        <Heading pb={2} size={{ base: "lg", md: "xl" }}>Set a new password</Heading>
        <Text fontSize="sm" color="gray.500" pb={6}>
          {viaMagicLink
            ? "Your account needs a new password. Please set one to continue."
            : "You're signing in with a temporary password. Please set a new password to continue."}
        </Text>
        <form onSubmit={handleSubmit}>
          <VStack gap={4} align="stretch">
            {mutation.error && (
              <Box bg="red.50" border="1px solid" borderColor="red.200" color="red.800" px={4} py={3} borderRadius="md" fontSize="sm">
                {getErrorMessage(mutation.error)}
              </Box>
            )}
            {!viaMagicLink && (
              <Field.Root>
                <Field.Label fontSize="xs" fontWeight="bold">Temporary password</Field.Label>
                <Input
                  name="old_password"
                  type="password"
                  placeholder="Enter your temporary password"
                  bg="white"
                  autoComplete="current-password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                  _focus={{ borderColor: "green.500" }}
                />
              </Field.Root>
            )}
            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">New password</Field.Label>
              <Input
                name="new_password1"
                type="password"
                placeholder="Enter new password"
                bg="white"
                autoComplete="new-password"
                value={newPassword1}
                onChange={(e) => setNewPassword1(e.target.value)}
                required
                _focus={{ borderColor: "green.500" }}
              />
            </Field.Root>
            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">Confirm new password</Field.Label>
              <Input
                name="new_password2"
                type="password"
                placeholder="Re-enter new password"
                bg="white"
                autoComplete="new-password"
                value={newPassword2}
                onChange={(e) => setNewPassword2(e.target.value)}
                required
                _focus={{ borderColor: "green.500" }}
              />
            </Field.Root>
            <Button
              type="submit"
              bg="green.800"
              color="white"
              _hover={{ bg: "green.900" }}
              size="lg"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Saving…" : "Set new password"}
            </Button>
          </VStack>
        </form>
      </Box>
    </>
  )
}

export default ForcePasswordChange
